import React, { useState, useEffect, useCallback, useMemo, useImperativeHandle, useRef } from "react";
import { useTransition, animated } from "@react-spring/web";
import { closestCenter, DndContext, DragCancelEvent, DragEndEvent, DragMoveEvent, DragOverEvent, DragOverlay, DragStartEvent, PointerSensor, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { SortableContext, useSortable, arrayMove, rectSortingStrategy,horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToWindowEdges, snapCenterToCursor } from "@dnd-kit/modifiers";

import Button from "src/components/button";
import Reveal from "src/components/animation";
import Actions from "src/components/actionbar";
import smartFilters from "./smartFilters";
import { EntityCard } from "../../selectedContentCard/entityCard";
import Slider from "src/components/slider";
import { EdithubContext } from "../index";

import _ from "lodash";
import cx from "classnames";
import styles from "./selectContent.module.scss";
import { useHubAdoption } from "src/redux/actions/adoption/hub";
import { EntityType } from "src/types/adoption/hub";
import { useSelector } from "react-redux";
import userRoleService from "src/utils/userRoles.service";
import { SectionState } from "src/pages/collection/types";
import { CollectionAsset } from "src/types/collections";
import { FileService } from "src/utils/file.service";
import Thumbnail from "src/components/thumbnail";
import { SectionTab } from "../../sectionTab";
import { HubLayoutSection } from "src/types/hub";
import { ScrollableContainer } from "src/components/scroller";
import { fixCursorSnapOffset } from "src/utils/dnd";


export type EntityTreeHandler = {
  handleEntityTreeCancel: () => void;
};

type SelectContentPropsType = {
  isTypeSelectionMode: boolean;
  setIsTypeSelectionMode: (value: boolean) => void;
};

const SelectContent = React.forwardRef<EntityTreeHandler, SelectContentPropsType>(({
  isTypeSelectionMode,
  setIsTypeSelectionMode
}, ref) => {

  const { rowItem, updateRowItem,
    activeColumnIndex, setActiveColumnIndex,
    persistedSelectedEntities,
    selectedEntity, setSelectedEntity,
    showEntityTree, setShowEntityTree,
    createSection, currentStep
  } = React.useContext(EdithubContext);

  const contentType = rowItem[activeColumnIndex]?.section?.entityType;
  const isCollectionFoldersLayout = rowItem[activeColumnIndex]?.section?.layoutStyle === "collection_folders"; // is collection folders layout

  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const isRecommendedAssetsEnabled = useSelector((state:any) => _.get(state.accounts, "item.recommendationEnabled", false));

  const [showSmartFilters, setShowSmartFilters] = useState<boolean>(false);
  const [showSelectedEntites, setShowSelectedEntites] = useState<boolean>(false);
  const [smartFilter, setSmartFilter] = useState<string>(
    "recently_viewed_streams",
  );
  const [showSlider, setShowSlider] = useState(false);
  const [activeDragEntityId, setActiveDragEntityId] = useState<string | number>();
  const [draggingSection, setDraggingSection] = useState<HubLayoutSection | null>(null);
  const [dragEntity, setDragEntity] = useState<any | null>(null); // cross section drag and drop for entity

  const isExistingLayout = useMemo(() => {
    return _.get(rowItem[activeColumnIndex], "section.id") ? true : false;
  }, [rowItem])

  const isUpdatingLayout = useMemo(() => {
    return _.get(rowItem[activeColumnIndex], "section.id") ? true : false;
  }, [rowItem])

  const { trackSmartFilterConfirmationScreenView, trackHubSectionContentSelectionTypeConfirmEvent, trackHubSectionContentSelectClicked,
    trackHubSectionContentSelectionTypeSelectedEvent, trackHubSectionManageContentClicked, trackSectionContentRemovedEvent } = useHubAdoption();

  useEffect(() => {
    if (!showSmartFilters && showSlider) {
      trackSmartFilterConfirmationScreenView();
    }
  }, [showSlider, showSmartFilters])

   useEffect(() => {
    const smartFilterValue = rowItem[activeColumnIndex]?.section?.smartFilter;
    setSmartFilter((smartFilterValue && smartFilterValue !== "unspecified") ? smartFilterValue : "recently_viewed_streams");
  }, [rowItem, activeColumnIndex])

  useEffect(() => {
    if (isUpdatingLayout) {
       const isSmartFilter = _.get(rowItem[activeColumnIndex], 'section.smartFilter') && _.get(rowItem[activeColumnIndex], 'section.smartFilter') != "unspecified" ? true : false;
       setIsTypeSelectionMode(false);
      if (isSmartFilter) {
        setShowSmartFilters(true);
        setShowSelectedEntites(false);
      } else {
        setShowSelectedEntites(true);
        setShowSmartFilters(false);
      }
    } else {
      setIsTypeSelectionMode(true);
      setShowSelectedEntites(false);
      setShowSmartFilters(false);
    }
  }, [contentType]);


  useEffect(() => { // Prevent empty state for "collection_folders" layout on switch
     const isCollectionFolders = rowItem[activeColumnIndex]?.section?.layoutStyle === "collection_folders";
     if (isCollectionFolders) {
      setIsTypeSelectionMode(false);
      setShowSelectedEntites(true);
      setShowSmartFilters(false);
    } 
  }, [rowItem])

  useEffect(() => {
     if(isCollectionFoldersLayout && createSection){ // for new section
        setIsTypeSelectionMode(false); 
        setShowSelectedEntites(true);
        setShowSmartFilters(false);
     }
  }, [isCollectionFoldersLayout, createSection])

  const handleEntityTreeCancel = () => {
     setShowEntityTree(false);

    const isUpdatingLayout = _.get(rowItem[activeColumnIndex], "section.id") ? true : false;

    if (!createSection && isUpdatingLayout && _.size(persistedSelectedEntities) > 0) {
      const updatedSelectedEntity = {
        ...selectedEntity,
        [activeColumnIndex]: persistedSelectedEntities[activeColumnIndex]
      };
      setSelectedEntity(updatedSelectedEntity);
    }
    else {
      const updatedSelectedEntity = {
        ...selectedEntity,
        [activeColumnIndex]: []
      };
      setSelectedEntity(updatedSelectedEntity);
    }
    if(!isCollectionFoldersLayout){
        setIsTypeSelectionMode(!isExistingLayout);
        setShowSelectedEntites(isExistingLayout);
    }

  }

  useImperativeHandle(ref, () => ({
    handleEntityTreeCancel,
  }));

  const handleInputChange = (value: string) => {
    updateRowItem("title", value)
  };

  const handleSliderConfirm = () => {
    setShowSlider(false);
    trackHubSectionContentSelectionTypeConfirmEvent(showSmartFilters ? "Manual" : "Smart Filter");
    if (showSmartFilters) {
      updateRowItem("smartFilter", null);
      setShowEntityTree(true)
      setShowSmartFilters(false)
      setShowSelectedEntites(true);
    } else {
      setShowSmartFilters(true)
      setShowEntityTree(false);
      setShowSelectedEntites(false);
      const firstFilter = smartFilters.smartFilters
        .filter((filter) => filter.contentType === contentType);
      updateRowItem("smartFilter", firstFilter[0]?.type);
    }
  }

  const transitions = useTransition(showSmartFilters, {
    from: { transform: "translateY(100%)", opacity: 0 },
    enter: { transform: "translateY(0%)", opacity: 1 },
    leave: { transform: "translateY(100%)", opacity: 0 },
  });

  const filtersSelectionTransition = useTransition(isTypeSelectionMode, {
    from: { transform: "translateY(100%)", opacity: 0 },
    enter: { transform: "translateY(0%)", opacity: 1 },
    leave: { transform: "translateY(100%)", opacity: 0 },
    config: { duration: 250 }
  });

  // New Section Creation Handler
  const AddSectionButton: React.FC<{
    onAdd: () => void;
  }> = ({ onAdd }) => {

    const handleClick = () => {
      onAdd();
    };
  
    return (
      <>
        <div className={styles.addSectionButton} onClick={handleClick}>
          <i className={cx(styles.icon, "paperflite-add")}></i>
          <div className={cx(styles.name, styles.add)}>Add Section</div>
        </div>
      </>
    );
  };

  const handleRemoveSection = (index: number) => {
    const updatedRowItem = rowItem.filter((_, i) => i !== index);
    updateRowItem("updateRowItem", updatedRowItem);

    // remove the selected entities for that section
    const prevSelectedEntity = { ...selectedEntity };
    delete prevSelectedEntity[index]
    setSelectedEntity({
        ...prevSelectedEntity,
    });
    if (activeColumnIndex >= updatedRowItem.length) {
      setActiveColumnIndex(updatedRowItem.length - 1);
    }
  };

  const handleAddSectionClick = () => { // create a new section
    const newSection = {
      width: 1,
      section: {
        title: `Untitled ${_.size(rowItem) + 1}`,
        cardStyle: "small",
        contents: [],
        entityType: "collection",
        layoutStyle: "collection_folders",
        type: "collections_folders",
        smartFilter: null,
      },
    };
    updateRowItem("addSection", newSection);
    setActiveColumnIndex(_.size(rowItem)); // set active to new section
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
        activationConstraint: { // to avoid accidental drags
          distance: 8,
        },
    })
  );

  const handleDragStart = (event: DragStartEvent) => { // Drag Start
    const activeType = _.get(event, "active.data.current.type");

    if (activeType === "section") { // section drag & drop
      const section = _.get(event, "active.data.current.section"); // get the section being dragged
      setDraggingSection(section);
      return;
    }

    if (activeType === "entity") { // entity drag & drop
      const entityId = _.get(event, "active.id"); 
      setActiveDragEntityId(entityId);

      const entity =  _.get(event, "active.data.current.entity");  // entity data
      const fromSectionId =  _.get(event, "active.data.current.sectionId");
      setDragEntity({
        asset: entity, // the entity being dragged
        fromSectionId, // where drag began
        overSectionId: fromSectionId, // start same as source
        overSection: activeType === 'section',
      });
      return;
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if(active && over){
      // section (i.e is only Folders)
      if (_.get(active, "data.current.type") === 'section' && _.get(over, "data.current.type") === "section") {
        const oldIndex = Number(_.get(active, "id")); // original tab index
        const newIndex = Number(_.get(over, "id")); // new tab index

        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
          // update the row items
          const updated = arrayMove([...rowItem], oldIndex, newIndex);
          updateRowItem("updateRowItem", updated);
          // update the selected entities mapping
          const updatedSelectedEntity = {};
          updated.forEach((item, idx) => {
            const oldIdx = rowItem.indexOf(item); // get old index
            updatedSelectedEntity[idx] = selectedEntity[oldIdx] || [];
          });

          setSelectedEntity(updatedSelectedEntity);
          setActiveColumnIndex(newIndex); // set active column index to new index
        }
      }

      // entity (items within a section)
      if(_.get(active, "data.current.type") === 'entity' && _.get(over, "data.current.type") === "entity"){ 
          const items = selectedEntity[activeColumnIndex]; // active section items(entities)
          if (active && over && _.get(active, "id", "") !== _.get(over, "id", "")) {
            const oldIndex = _.findIndex(items as any, (item) => _.get(item, 'id') === active.id);
            const newIndex = _.findIndex(items as any, (item) => _.get(item, 'id') === over.id);
            const updatedArray = arrayMove(items as any, oldIndex, newIndex);

            const updatedSelectedEntity = {
              ...selectedEntity,
              [activeColumnIndex]: updatedArray,
            }
            setSelectedEntity(updatedSelectedEntity);
          }
      }

      // cross section drag
      if(_.get(active, "data.current.type") === 'entity' && _.get(over, "data.current.type") === "section"){
        const { fromSectionId, overSectionId, asset } = dragEntity;
        if(fromSectionId === undefined || overSectionId === undefined) return;

        if(fromSectionId !== overSectionId){ // different sections
          const entityId = asset.id; // entity id
          const fromItems = [...selectedEntity[fromSectionId]]; // from section items
          const toItems   = [...selectedEntity[overSectionId]]; // to section items

          const alreadyExistsInTarget = toItems.findIndex(i => i.id === entityId) !== -1; // check if item already exists in target section to avoid duplicates
          const oldIndex = fromItems.findIndex(i => i.id === entityId);
          const [moved] = fromItems.splice(oldIndex, 1); // splice from source and get moved item

          if(!alreadyExistsInTarget){ // avoid duplicates
            toItems.push(moved); // push to target section
          }

          setSelectedEntity({
                ...selectedEntity,
                [fromSectionId]: fromItems, // updated from section (origin)
                ...(!alreadyExistsInTarget && {[overSectionId]: toItems}), // updated to target section
          });
         }
      }
    }

    handleDragReset(); // reset
  };

  const handleDragReset = () => { // reset the drag state
    setActiveDragEntityId("");
    setDragEntity(null);
    setDraggingSection(null);
  }

  const handleDragMove = (event: DragMoveEvent) => {
    const { over } = event;

    if (!over) return;

    if(_.get(over, 'data.current.type') === 'section') {
      const overSectionId = _.get(over, 'id'); // section id(index)
      if (overSectionId !== undefined && overSectionId !== dragEntity?.fromSection){ // check and allow cross section drag and drop
        setDragEntity((prev: any) => ({
          ...prev,
          overSectionId: overSectionId,
          overSection: true,
        }));
      }
    } else {
      setDragEntity((prev: any) => ({
          ...prev,
          overSectionId: prev.fromSectionId,
          overSection: false,
      }));
    }
  };

  const handleSectionInputChange = (value: string, index: number) => {
    setActiveColumnIndex(index);
    updateRowItem("title", value);
  };

  const getActiveDragEntity = useMemo(() => {

    const entities = selectedEntity[activeColumnIndex] as any;
    const activeDragEntity = _.find(entities, (item) => _.get(item, "id", "") === activeDragEntityId)

    return { activeDragEntity };
    
  }, [activeDragEntityId, selectedEntity, activeColumnIndex])

  const { activeDragEntity } = getActiveDragEntity;

  const getItems = useMemo(() => {
    if(_.size(selectedEntity[activeColumnIndex]) > 0){
      return selectedEntity[activeColumnIndex]?.map(entity => _.get(entity, "id",""))
    }
    return [];
  },[selectedEntity])
  

  const renderSelectContent = useCallback(
    () => (
     <DndContext
        sensors={sensors}
        collisionDetection={fixCursorSnapOffset}
        onDragEnd={handleDragEnd}
        onDragMove={handleDragMove}
        onDragStart={handleDragStart}
        onDragCancel={handleDragReset}
      > 
      <div className={styles.pickFilter}>

        { isCollectionFoldersLayout && currentStep === 1 && renderCollectionFoldersTabs()}  {/* Render Folders for collection folders */}

        {!showEntityTree && !isCollectionFoldersLayout && <div className={cx(styles.switchContent, {
          [styles.showAddAssetIcon]: !showEntityTree && showSelectedEntites,
        })}>
          <Reveal show={true}>
            <div className={styles.messageSection}>
        
              <p className={styles.messageText}>
                Switch to <br />
                <b>{showSmartFilters ? "Manual Selection" : "Smart Filters"}</b>
              </p>
              <Actions.IconButton
                icon="paperflite-reverse"
                onClick={() =>
                  setShowSlider(true)
                }
              />
              

              <Actions.Button
                width={3}
                className={styles.addIcon}
                icon="paperflite-add"
                title="Add Content"
                onClick={() => {
                  trackHubSectionManageContentClicked();
                  setShowEntityTree(true)}
                }
              />
            </div>
          </Reveal>
        </div>}

        {showSelectedEntites &&
          <div className={styles.selectedContent}>
            {_.size(selectedEntity[activeColumnIndex]) === 0 && _.times(3, (index) => (
              <div key={index} className={styles.placeholderCard} />
            ))}

              <SortableContext items={getItems} strategy={rectSortingStrategy}>
                  {_.size(selectedEntity[activeColumnIndex]) > 0 && _.size(selectedEntity[activeColumnIndex]) > 0 &&
                    selectedEntity[activeColumnIndex].map((entity, index) => {
                      return <EntityCard
                        key={entity?.id}
                        entity={entity}
                        activeDragEntityId={activeDragEntityId}
                        onRemove={() => {
                          const prevItems = selectedEntity[activeColumnIndex];
                          const updatedItems = _.reject(prevItems, { id: entity.id });
                          const updatedSelectedEntity = {
                            ...selectedEntity,
                            [activeColumnIndex]: updatedItems,
                          }
                          setSelectedEntity(updatedSelectedEntity);
                          trackSectionContentRemovedEvent(entity.entityType as EntityType)
                        }}
                        sectionId={activeColumnIndex}
                      />
                    })}
              </SortableContext>
              <DragOverlay modifiers={[snapCenterToCursor]} dropAnimation={null} adjustScale={false}>
                {activeDragEntity && activeDragEntityId && <DraggingEntityCard entity={activeDragEntity} overSection={dragEntity.overSection} />}
              </DragOverlay>

             <Reveal show={_.size(selectedEntity[activeColumnIndex]) === 0 && !showEntityTree && !isCollectionFoldersLayout}>

              <div className={cx(styles.filterOption, styles.manualSelectionPlaceholder)}>
                <img
                  src="/images/hub/editLayout/selectContent.svg"
                  alt="Manual Selection"
                />
                <h2>Manual Selection</h2>
                <p>
                  Choose each content piece to be displayed in the section by yourself. Ideal when you want full control (hello, control freaks).
                </p>
                <Button
                  className={styles.filterButton}
                  icon="paperflite-add"
                  text="Add Content"
                  variant="primary"
                  onClick={() => {
                    setShowEntityTree(true);
                    setShowSmartFilters(false);
                    setIsTypeSelectionMode(false);
                    setShowSelectedEntites(true);
                    updateRowItem("smartFilter", null);
                    trackHubSectionContentSelectClicked();
                  }}
                />
              </div>
            </Reveal>

          </div>}

        {showSmartFilters && !isCollectionFoldersLayout && <div className={styles.smartFilterContainer}>
          <p className={styles.pickFilterHeading}>Pick a smart filter</p>
          <p className={styles.pickFilterSubHeading}>
          Let our smart filters do the heavy lifting. Pick a filter, grab a coffee.
          </p>

          {transitions(
            (style, item) =>
              item && (
                <animated.div style={style} className={styles.filters}>
                  {smartFilters.smartFilters
                    .filter((filter) => filter.contentType === contentType)
                    .map((filter) => {
                      if((!isRecommendedAssetsEnabled && filter.type === "recommended_assets") || (!userRoleService.canShare() && filter.type=== "frequently_shared_assets")){
                        return <></>
                      }
                    return (
                      <div
                      key={filter.name}
                      className={cx(
                        styles.filterItem,
                        filter.type === smartFilter &&
                        styles.activeFilterItem,
                      )}
                      onClick={() => {
                        updateRowItem("smartFilter", filter.type);
                      }}
                    >
                      <i
                        className={cx(
                          filter.type === smartFilter
                            ? "fa-solid fa-check-circle"
                            : "paperflite-radio-button-unselected",
                          styles.selectIcon
                        )}
                        aria-hidden="true"
                      ></i>
                      <img
                        src={filter.icon}
                        alt="Smart Filters"
                        className={styles.filterImage}
                      />
                      <h3 className={styles.filterName}>{filter.name}</h3>
                      <p className={styles.filterDescription}>
                        {filter.description}
                      </p>
                    </div>
                    )
                    }
                    )}
                </animated.div>
              ),
          )}
        </div>}


        <Slider.Dialog show={showSlider} width="400" onClose={() => setShowSlider(false)}>
          <Slider.Header
            title={`Switch to: “${showSmartFilters ? "manual selection" : "smart Filters"}”`}
          >
          </Slider.Header>

          <Slider.Body className={styles.sliderBody}>
            <img
              className={styles.sliderImage}
              src={`/images/hub/sliders/${showSmartFilters ? "manualSelection" : "smartFilter"}.svg`}
              alt="Smart Filters"
            />
            {showSmartFilters ? "Ready to go full content curator? You'll handpick assets without our smart filter training wheels."
              : "Let our smart filters play your content matchmaker. Sure you want to continue?"}
          </Slider.Body>

          <Slider.Actions>
            <Button variant="primary"
              icon="paperflite-tick"
              text={'Confirm'}
              disabled={false}
              onClick={handleSliderConfirm}
            />
            <Button text="Cancel" disabled={false} onClick={() => setShowSlider(false)} />
          </Slider.Actions>
        </Slider.Dialog>

        </div >
     </DndContext>
    ),
    [smartFilter, contentType, transitions, activeColumnIndex],
  );

  const renderCollectionFoldersTabs = () => {
    return( 
    <div className={styles.collectionSectionsColumnInputs}>
      <div className={styles.collectionSectionsInputs}>
        <AddSectionButton onAdd={handleAddSectionClick} />
                {/* sections */}
                  <SortableContext
                    items={rowItem
                      .map((column, index) => index)}
                    strategy={horizontalListSortingStrategy}
                  >
                    <div className={styles.scrollContainer}>
                      <ScrollableContainer
                          distance={300}
                    >
                       <div className={styles.lists}>
                          {_.size(rowItem) > 0 && 
                            rowItem.map((column, index) => {
                              return <SectionTab
                                key={index}
                                sectionId={index}
                                section={column.section}
                                selected={index === activeColumnIndex}
                                isRemovable={index >= 2} // first two sections are not removable
                                onClick={() => setActiveColumnIndex(index)}
                                onNameChange={(value) => handleSectionInputChange(value, index)}
                                onRemove={() => handleRemoveSection(index)}
                              />
                            })}
                        </div>
                      </ScrollableContainer>
                    </div>
                   </SortableContext>

                <DragOverlay modifiers={[snapCenterToCursor]} dropAnimation={null} adjustScale={false}>
                   {draggingSection ? (
                    <DraggingSectionCard section={draggingSection} />
                    ) : null}
                </DragOverlay>

      </div>

      { !showEntityTree && <Actions.Button
                width={3}
                className={styles.addContent}
                icon="paperflite-add"
                title={"Add Content"}
                onClick={() => {
                  trackHubSectionManageContentClicked();
                  setShowEntityTree(true)}
                }
        />}
    </div>
  )};
  
  const handleSwitchSection = (index: number) => {
    if (index === activeColumnIndex || showEntityTree) return;
    setActiveColumnIndex(index);

   // const hasSelectedEntities = selectedEntity[index]?.length > 0; // why ??? this statement
    const isSectionUpdated = rowItem[index]?.section?.tempId ? true : false;

    if (isSectionUpdated || isExistingLayout) {
      //const isSmartFilter = rowItem[index]?.section?.smartFilter ? true : false;
       const isSmartFilter =  _.get(rowItem[index], "section.smartFilter") && _.get(rowItem[index], "section.smartFilter") !== "unspecified"  ? true : false;
      if (isSmartFilter) {
        setShowSmartFilters(true);
        setShowSelectedEntites(false);
      } else {
        setShowSelectedEntites(true);
        setShowSmartFilters(false);
        setShowEntityTree(true);
      }
      setIsTypeSelectionMode(false);
    } else {
      setShowSelectedEntites(false);
      setShowSmartFilters(false);
      setIsTypeSelectionMode(true);
    }
  };

  return (
    <animated.div>
      <div className={styles.smartFilters}> 
       { !isCollectionFoldersLayout &&
       <>
       <div className={cx(styles.columnInputs)}>
            {rowItem.map((column, index) => (
              <div
                key={index}
                className={cx(styles.columnInput, {
                  [styles.showDivider]: _.size(rowItem) > 1,
                })}
                onClick={() => handleSwitchSection(index)}
              >
                <input
                  className={cx(styles.inputField, {
                    [styles.activeInput]: index === activeColumnIndex,
                    [styles.disabledInput]: showEntityTree && index !== activeColumnIndex,
                  })}
                  placeholder="Add a section name"
                  value={column.section.title}
                  onChange={(e) => handleSectionInputChange(e.target.value, index)}
                />
              </div>
            ))}
        </div>
      
        {filtersSelectionTransition((style, item) =>
          item && (<animated.div style={style} className={styles.filtersContainer}>

            <div className={styles.filterOption}>
              <img
                src="/images/hub/editLayout/smartFilters.svg"
                alt="Smart Filters"
              />
              <h2>Smart Filters</h2>
              <p>
                Leave the content selection to our smart content filters. Showcase dynamic content based on the smart filter picked.
              </p>
              <Button
                className={styles.filterButton}
                icon="paperflite-filter"
                text="Choose filter"
                variant="primary"
                onClick={() => {
                  setShowSmartFilters(true);
                  setShowEntityTree(false);
                  setIsTypeSelectionMode(false);
                  setShowSelectedEntites(false);
                  const firstFilter = smartFilters.smartFilters
                    .filter((filter) => filter.contentType === contentType);
                  updateRowItem("smartFilter", firstFilter[0]?.type);
                  trackHubSectionContentSelectionTypeSelectedEvent("Smart Filter");
                }}
              />
            </div> 

           <div className={styles.divider}>or</div>

            <div className={styles.filterOption}>
              <img
                src="/images/hub/editLayout/selectContent.svg"
                alt="Manual Selection"
              />
              <h2>Manual Selection</h2>
              <p>
                Choose each content piece manually. For when you want total control.
              </p>
              <Button
                className={styles.filterButton}
                icon="paperflite-add"
                text="Add Content "
                variant="primary"
                onClick={() => {
                  setShowEntityTree(true);
                  setShowSmartFilters(false);
                  setIsTypeSelectionMode(false);
                  setShowSelectedEntites(true);
                  updateRowItem("smartFilter", null);
                  trackHubSectionContentSelectClicked();
                  trackHubSectionContentSelectionTypeSelectedEvent("Manual");
                }}
              />
            </div>
        </animated.div>))} 
        </>} 
        {!isTypeSelectionMode && renderSelectContent()}
      </div>
    </animated.div>
  );
});

export default SelectContent;

const DraggingSectionCard: React.FC<{ section: HubLayoutSection }> = ({
  section,
}) => {
  return (
    <div className={cx(styles.draggingSectionCard, styles.dragPreview, "collection_section")}>
      {section.title}
    </div>
  );
};

const DraggingEntityCard: React.FC<{ entity: CollectionAsset, overSection: boolean }> = ({ entity, overSection }) => {
  return (
    <div className={cx(styles.card, styles.dragging, { [styles.overSection]: overSection })}>
     { !overSection && <EntityCard entity={entity} />}
      { overSection && <div className={styles.message}>Add to section</div>}
    </div>
  );
}
